import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { Home } from './pages/Home';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { CookieConsent } from './components/CookieConsent';
import { useRouteTracking } from './hooks/useRouteTracking';

/** Null-rendering component that fires virtual PageViews on every SPA route change. */
function RouteTracker() {
  useRouteTracking();
  return null;
}

// Lazy-loaded routes — only downloaded when the user navigates to them
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const Signup = lazy(() => import('./pages/Signup').then(m => ({ default: m.Signup })));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import('./pages/ResetPassword').then(m => ({ default: m.ResetPassword })));
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Reviews = lazy(() => import('./pages/Reviews').then(m => ({ default: m.Reviews })));
const QRCodes = lazy(() => import('./pages/QRCodes').then(m => ({ default: m.QRCodes })));
const QRCodeEditor = lazy(() => import('./pages/QRCodeEditor').then(m => ({ default: m.QRCodeEditor })));
const QRCodeDesigner = lazy(() => import('./pages/QRCodeDesigner').then(m => ({ default: m.QRCodeDesigner })));
const Analytics = lazy(() => import('./pages/Analytics').then(m => ({ default: m.Analytics })));
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const Pricing = lazy(() => import('./pages/Pricing').then(m => ({ default: m.Pricing })));
const MarketingHub = lazy(() => import('./pages/MarketingHub').then(m => ({ default: m.MarketingHub })));
const PublicFeedback = lazy(() => import('./pages/PublicFeedback').then(m => ({ default: m.PublicFeedback })));
const PublicWall = lazy(() => import('./pages/PublicWall').then(m => ({ default: m.PublicWall })));
const PublicDemo = lazy(() => import('./pages/PublicDemo').then(m => ({ default: m.PublicDemo })));
const FeedbackThanks = lazy(() => import('./pages/FeedbackThanks').then(m => ({ default: m.FeedbackThanks })));
const SuccessCelebration = lazy(() => import('./pages/SuccessCelebration').then(m => ({ default: m.SuccessCelebration })));
const TermsOfService = lazy(() => import('./pages/TermsOfService').then(m => ({ default: m.TermsOfService })));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy').then(m => ({ default: m.CookiePolicy })));
const IndustrySolution = lazy(() => import('./pages/IndustrySolution').then(m => ({ default: m.IndustrySolution })));
const FeaturePage = lazy(() => import('./pages/FeaturePage').then(m => ({ default: m.FeaturePage })));
const ReviewFilter = lazy(() => import('./pages/ReviewFilter').then(m => ({ default: m.ReviewFilter })));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
    <div className="animate-spin rounded-full h-10 w-10 border-2 border-emerald-500 border-t-transparent"></div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RouteTracker />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* --- PUBLIC ROUTES --- */}

            {/* 1. The Landing Page (Root URL) — eagerly loaded for speed */}
            <Route path="/" element={<Home />} />

            {/* 2. Authentication */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* 3. Public Tools (Feedback Forms, Walls) */}
            <Route path="/s/:qrCodeId" element={<PublicFeedback />} />
            <Route path="/feedback/:qrCodeId" element={<PublicFeedback />} /> {/* Legacy support */}
            <Route path="/feedback-thanks" element={<FeedbackThanks />} />
            <Route path="/celebration" element={<SuccessCelebration />} />
            <Route path="/wall/:businessId" element={<PublicWall />} />
            <Route path="/demo/:businessId" element={<PublicDemo />} />

            {/* 4. Legal Pages */}
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/cookies" element={<CookiePolicy />} />

            {/* 4b. Spanish Legal Pages (same content, localized URLs) */}
            <Route path="/es/terminos" element={<TermsOfService />} />
            <Route path="/es/privacidad" element={<PrivacyPolicy />} />
            <Route path="/es/cookies" element={<CookiePolicy />} />

            {/* 5. Programmatic SEO — Industry Solution Pages */}
            <Route path="/solutions/:slug" element={<IndustrySolution />} />
            <Route path="/es/soluciones/:slug" element={<IndustrySolution locale="es" />} />

            {/* 6. Feature Landing Pages */}
            <Route path="/features/:slug" element={<FeaturePage />} />

            {/* 7. Review Filter Sub-Page */}
            <Route path="/review-filter" element={<ReviewFilter />} />

            {/* --- PROTECTED APP ROUTES --- */}
            {/* All routes inside here require login and show the Sidebar Layout */}
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="/qr-codes" element={<QRCodes />} />
              <Route path="/qr-codes/new" element={<QRCodeEditor />} />
              <Route path="/qr-codes/:id/edit" element={<QRCodeEditor />} />
              <Route path="/qr-codes/design" element={<QRCodeDesigner />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/marketing" element={<MarketingHub />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/pricing" element={<Pricing />} />
            </Route>

            {/* Fallback: Any unknown URL goes back to Home */}
            <Route path="*" element={<Navigate to="/" replace />} />

          </Routes>
        </Suspense>

        {/* Cookie Consent Banner */}
        <CookieConsent />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
